import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Power } from "lucide-react";

export default function AdminCountries() {
  const [countries, setCountries] = useState([]);
  const [name, setName] = useState("");
  const [apiId, setApiId] = useState("");
  const [standardPrice, setStandardPrice] = useState("");
  const [edits, setEdits] = useState({});

  const load = () => api.get("/admin/countries").then((r) => setCountries(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name || apiId === "") return;
    try {
      await api.post("/admin/countries", {
        country_name: name,
        api_country_id: parseInt(apiId),
        standard_price: standardPrice === "" ? null : parseFloat(standardPrice),
      });
      toast.success("Eklendi");
      setName("");
      setApiId("");
      setStandardPrice("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const save = async (c) => {
    const edit = edits[c.id];
    if (!edit) return;
    try {
      await api.put(`/admin/countries/${c.id}`, {
        country_name: edit.name ?? c.country_name,
        api_country_id: parseInt(edit.api_id ?? c.api_country_id),
        standard_price: edit.standard_price !== undefined
          ? (edit.standard_price === "" ? null : parseFloat(edit.standard_price))
          : (c.standard_price ?? null),
      });
      toast.success("Güncellendi");
      setEdits({ ...edits, [c.id]: undefined });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    try {
      await api.delete(`/admin/countries/${id}`);
      toast.success("Silindi");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggle = async (id) => {
    try {
      await api.post(`/admin/countries/${id}/toggle`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Eşleştirme</div>
        <h1 className="font-display text-3xl font-black">Ülkeler</h1>
        <p className="text-white/60 text-sm mt-2">Grizzly API'deki ülke ID'lerini ülke isimleriyle eşleştirin.</p>
      </div>

      <form onSubmit={add} className="border border-white/10 bg-[hsl(0,0%,7%)] p-5 mb-6 flex flex-wrap gap-3 items-end" data-testid="add-country-form">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">Ülke Adı</label>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="new-country-name" className="w-full mt-1 bg-transparent border border-white/15 focus:border-[hsl(221,83%,53%)] outline-none px-3 py-2 text-sm rounded-sm" />
        </div>
        <div className="w-40">
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">API Ülke ID</label>
          <input value={apiId} onChange={(e) => setApiId(e.target.value)} type="number" data-testid="new-country-api-id" className="w-full mt-1 bg-transparent border border-white/15 focus:border-[hsl(221,83%,53%)] outline-none px-3 py-2 text-sm rounded-sm font-mono" />
        </div>
        <div className="w-40">
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">Satış Fiyatı TL</label>
          <input
            value={standardPrice}
            onChange={(e) => setStandardPrice(e.target.value)}
            type="number"
            step="0.01"
            placeholder="Otomatik"
            className="w-full mt-1 bg-transparent border border-white/15 focus:border-[hsl(221,83%,53%)] outline-none px-3 py-2 text-sm rounded-sm font-mono"
          />
        </div>
        <button type="submit" data-testid="add-country-btn" className="bg-[hsl(221,83%,53%)] hover:bg-[hsl(221,83%,58%)] text-white text-sm px-4 py-2 uppercase tracking-wider font-bold transition-colors">
          <Plus className="w-3.5 h-3.5 inline mr-1" />Ekle
        </button>
      </form>

      <div className="border border-white/10 bg-[hsl(0,0%,7%)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.2em] text-white/40 border-b border-white/10">
            <tr>
              <th className="text-left py-3 px-4 font-bold">Ülke Adı</th>
              <th className="text-left px-4 font-bold">API ID</th>
              <th className="text-left px-4 font-bold">Satış Fiyatı TL</th>
              <th className="text-left px-4 font-bold">Durum</th>
              <th className="text-right px-4 font-bold">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {countries.map((c) => {
              const e = edits[c.id] || {};
              return (
                <tr key={c.id} className="border-b border-white/5">
                  <td className="py-2 px-4">
                    <input
                      value={e.name ?? c.country_name}
                      onChange={(ev) => setEdits({ ...edits, [c.id]: { ...e, name: ev.target.value } })}
                      className="bg-transparent border border-white/10 focus:border-[hsl(221,83%,53%)] outline-none px-2 py-1 text-sm rounded-sm w-full"
                    />
                  </td>
                  <td className="px-4">
                    <input
                      type="number"
                      value={e.api_id ?? c.api_country_id}
                      onChange={(ev) => setEdits({ ...edits, [c.id]: { ...e, api_id: ev.target.value } })}
                      className="bg-transparent border border-white/10 focus:border-[hsl(221,83%,53%)] outline-none px-2 py-1 text-sm rounded-sm w-24 font-mono"
                    />
                  </td>
                  <td className="px-4">
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Otomatik"
                      value={e.standard_price ?? (c.standard_price ?? "")}
                      onChange={(ev) => setEdits({
                        ...edits,
                        [c.id]: { ...e, standard_price: ev.target.value }
                      })}
                      className="bg-transparent border border-white/10 focus:border-[hsl(221,83%,53%)] outline-none px-2 py-1 text-sm rounded-sm w-28 font-mono"
                    />
                  </td>
                  <td className="px-4">
                    <button onClick={() => toggle(c.id)} className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${c.enabled ? "text-[hsl(142,71%,55%)] border-[hsl(142,71%,55%)]/40" : "text-white/40 border-white/20"}`}>
                      {c.enabled ? "Aktif" : "Pasif"}
                    </button>
                  </td>
                  <td className="px-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => save(c)} className="border border-white/15 hover:border-[hsl(221,83%,53%)] hover:text-[hsl(221,83%,63%)] text-xs px-2 py-1 uppercase tracking-wider transition-colors" data-testid={`save-country-${c.id}`}>
                        <Save className="w-3 h-3 inline mr-1" />Kaydet
                      </button>
                      <button onClick={() => del(c.id)} className="border border-white/15 hover:border-red-500 hover:text-red-400 text-xs px-2 py-1 uppercase tracking-wider transition-colors" data-testid={`delete-country-${c.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
